import { useState } from "react";

// Strong random password: 20 chars from a mixed set, drawn with the
// platform CSPRNG. Guarantees ≥1 of each class so it always passes the
// ≥8 length + any downstream complexity expectations.
const SETS = {
  lower: "abcdefghijkmnpqrstuvwxyz",
  upper: "ABCDEFGHJKLMNPQRSTUVWXYZ",
  digit: "23456789",
  symbol: "!@#$%^&*()-_=+[]{}",
};
function randomPassword(len = 20): string {
  const all = SETS.lower + SETS.upper + SETS.digit + SETS.symbol;
  const pick = (set: string) => {
    const a = new Uint32Array(1);
    crypto.getRandomValues(a);
    return set[a[0] % set.length];
  };
  const chars = [pick(SETS.lower), pick(SETS.upper), pick(SETS.digit), pick(SETS.symbol)];
  while (chars.length < len) chars.push(pick(all));
  // Fisher–Yates shuffle so the guaranteed class chars aren't front-loaded.
  for (let i = chars.length - 1; i > 0; i--) {
    const r = new Uint32Array(1);
    crypto.getRandomValues(r);
    const j = r[0] % (i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
}

function EyeIcon({ off }: { off: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
      {off && <line x1="3" y1="3" x2="21" y2="21" />}
    </svg>
  );
}

function DiceIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <circle cx="8" cy="8" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="16" cy="8" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="8" cy="16" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="16" cy="16" r="1.2" fill="currentColor" stroke="none" />
    </svg>
  );
}

/**
 * Password field with two right-aligned controls pinned inside the
 * border on the same line (flex layout, not absolute positioning):
 *   - a dice button that fills the field with a strong random password
 *     (and reveals it so the operator can copy it), and
 *   - an eye toggle that shows/hides the value (consistent eye / eye-off
 *     line icons, never emoji).
 * Presentational — the caller owns value + onChange.
 */
export function PasswordInput({
  value,
  onChange,
  onEnter,
  placeholder = "password",
  autoFocus,
  className = "",
  allowGenerate = true,
}: {
  value: string;
  onChange: (next: string) => void;
  onEnter?: () => void;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
  allowGenerate?: boolean;
}) {
  const [reveal, setReveal] = useState(false);
  return (
    <div
      className={`flex items-center rounded border border-line bg-bg focus-within:border-accent ${className}`}
    >
      <input
        type={reveal ? "text" : "password"}
        autoComplete="new-password"
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && onEnter) {
            e.preventDefault();
            onEnter();
          }
        }}
        minLength={8}
        maxLength={128}
        placeholder={placeholder}
        className="min-w-0 flex-1 bg-transparent px-2 py-1.5 font-mono text-sm focus:outline-none"
      />
      {allowGenerate && (
        <button
          type="button"
          onClick={() => {
            onChange(randomPassword());
            setReveal(true);
          }}
          aria-label="generar contraseña aleatoria"
          title="generar aleatoria"
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-mute hover:text-accent"
        >
          <DiceIcon />
        </button>
      )}
      <button
        type="button"
        onClick={() => setReveal((r) => !r)}
        aria-label={reveal ? "ocultar contraseña" : "mostrar contraseña"}
        title={reveal ? "ocultar" : "mostrar"}
        className="mr-1 flex h-6 w-6 shrink-0 items-center justify-center rounded text-mute hover:text-accent"
      >
        <EyeIcon off={reveal} />
      </button>
    </div>
  );
}
